import React, { useEffect, useState } from 'react';
import { BasecampLayout } from '../../components/Layout';
import { PageHeader, Card, Spinner, Alert, Table, Btn } from '../../components/UI';

// Kandidatski deo (CAND rola) — My Journey, My Formation, My Assessments,
// My Class. Sadržaj i ton prate mockup (journey/formation/assessments/cfaclass
// views); podaci dolaze sa /api/basecamp/cfa/my-journey.

import { api } from '../../utils/api';

function useJourney() {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    api.cfa.getMyJourney()
      .then(d => setState({ loading: false, error: null, data: d }))
      .catch(e => setState({ loading: false, error: e.message, data: null }));
  }, []);
  return state;
}

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;
}

function Dot({ state }) {
  const colors = { done: 'var(--ink)', prog: 'var(--ink-soft)', todo: 'var(--canvas-warm)' };
  return (
    <span style={{
      display: 'inline-block', width: 14, height: 14, borderRadius: '50%',
      background: colors[state], border: state === 'todo' ? '1.5px solid var(--ink-faint)' : 'none',
      marginRight: 12, flexShrink: 0,
    }} />
  );
}

function monthOf12(firstContactDate) {
  if (!firstContactDate) return null;
  const start = new Date(firstContactDate);
  const months = Math.max(1, Math.min(12,
    Math.floor((Date.now() - start.getTime()) / (30.44 * 24 * 3600 * 1000)) + 1));
  return months;
}

// ── My Journey ─────────────────────────────────────────────────────────────
export function Journey() {
  const { loading, error, data } = useJourney();
  return (
    <BasecampLayout>
      <PageHeader title="My Journey — the approach march to Base Camp" />
      {loading ? <Loading /> : error ? <Alert type="error">{error}</Alert> : (() => {
        const c = data.candidate;
        const stageIdx = data.stages.findIndex(s => s.key === c.stage);
        const month = monthOf12(c.firstContactDate);
        const pct = Math.round(((stageIdx + 0.5) / data.stages.length) * 100);
        return (
          <>
            {month && (
              <Alert type="info">
                Month <b>{month} of 12</b> — the goal: from first conversation to your first delivered
                programme within one year.
              </Alert>
            )}
            <div style={{ background: 'var(--canvas-warm)', borderRadius: 8, height: 10, margin: '16px 0 24px' }}>
              <div style={{ background: 'var(--ink)', height: 10, borderRadius: 8, width: `${pct}%` }} />
            </div>

            <Card style={{ padding: '22px 24px', marginBottom: 24 }}>
              {data.stages.map((s, i) => (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', fontSize: '0.92rem' }}>
                  <Dot state={i < stageIdx ? 'done' : i === stageIdx ? 'prog' : 'todo'} />
                  <span style={{ fontWeight: i === stageIdx ? 700 : 400 }}>
                    {s.label}
                    {s.key === 'certified' && c.targetCert ? ` — onsite (target: ${c.targetCert})` : ''}
                    {i === stageIdx ? ' — in progress' : ''}
                  </span>
                </div>
              ))}
            </Card>

            <div className="grid-2col" style={{ gap: 24 }}>
              <Card style={{ padding: '22px 24px' }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 12 }}>Who walks with me</h3>
                <div style={{ fontSize: '0.9rem', lineHeight: 2 }}>
                  <div>▪ Mentor: <b>{c.mentor || '—'}</b></div>
                  <div>▪ Interviewer: <b>{c.interviewer || '—'}</b></div>
                  {data.cfaLeader && <div>▪ CFA Leader: <b>{data.cfaLeader}</b> — runs the class sessions</div>}
                  {c.className && <div>▪ {c.className} — see My Class</div>}
                  <div>▪ Business Partner: <b>{c.bp}</b></div>
                </div>
              </Card>
              <Card style={{ padding: '22px 24px' }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 12 }}>My documents</h3>
                <div style={{ fontSize: '0.9rem', lineHeight: 2, color: 'var(--ink-soft)' }}>
                  <div>▪ Being an Ice Pilot</div>
                  <div>▪ Development Path</div>
                  <div>▪ Ice Pilot Agreement — draft, for the agreement stage</div>
                </div>
              </Card>
            </div>
          </>
        );
      })()}
    </BasecampLayout>
  );
}

// ── My Formation ───────────────────────────────────────────────────────────
// Kurikulum je za sada fiksan (iz mockup-a); per-step progress dolazi sa
// Learning Paths mehanizmom. FormationProgress (x/10) vodi CFA Leader.
const CFA_STEPS = [
  ['S', 'Read: Our Story & Code of Honour (HB World)', '/hbworld/story'],
  ['D', 'Study (teaser): (IE017) The Learning Cycle', '/deliver/units/IE017'],
  ['S', 'Assignment: 5 conversations about development needs in your network', null],
  ['D', 'Study (teaser): (IE001) Visions Determine Behaviour', '/deliver/units/IE001'],
  ['D', 'Practice: deliver the Plank segment to your class (online)', null],
  ['S', 'Study (teaser): (IE006) Positive Attitude — WAIEM', '/deliver/units/IE006'],
  ['S', 'Homework: WAIEM sheet 3× daily for 21 days', null],
  ['D', 'Study (teaser): (IE010) Conscious Listening', '/deliver/units/IE010'],
  ['S', 'Assignment: practise diagnostic questions with your mentor', null],
  ['D', 'Practice delivery day with the class — observed by mentor', null],
];

export function Formation() {
  const { loading, error, data } = useJourney();
  return (
    <BasecampLayout>
      <PageHeader title="My Formation" subtitle="CFA — Candidates Formation & Assessment" />
      {loading ? <Loading /> : error ? <Alert type="error">{error}</Alert> : (
        <>
          <Alert type="info">
            You are living the methodology you will later deliver: sessions in a 3–4 week rhythm, homework
            in between, behaviour before theory. Two tracks run side by side — <b>S</b> the selling craft,
            <b> D</b> the delivery craft. Progress feeds your certification readiness.
          </Alert>
          <div style={{ margin: '18px 0', fontSize: '0.9rem', color: 'var(--ink-soft)' }}>
            The curriculum — <b>{data.candidate.formationProgress}/{data.candidate.formationTotal} completed</b> (confirmed by your CFA Leader)
          </div>
          <Card style={{ padding: '10px 18px' }}>
            <Table
              headers={['Track', 'Step', 'Status']}
              rows={CFA_STEPS.map(([track, step, href], i) => [
                <b>{track}</b>,
                href ? <a href={href} style={{ textDecoration: 'underline' }}>{step}</a> : step,
                i < data.candidate.formationProgress ? '✓ completed' : 'to do',
              ])}
            />
          </Card>
        </>
      )}
    </BasecampLayout>
  );
}

// ── My Assessments ─────────────────────────────────────────────────────────
export function Assessments() {
  const { loading, error, data } = useJourney();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function requestSelfAssessment() {
    setSending(true);
    try { await api.requestAssessment('self'); setSent(true); }
    catch (e) { alert(e.message); }
    finally { setSending(false); }
  }

  const hexacoLabels = {
    pending: 'not yet started', invited: 'invitation sent — complete before your interview',
    completed: 'completed — debrief opens your orientation', debriefed: 'completed · debriefed',
  };
  return (
    <BasecampLayout>
      <PageHeader title="My Assessments" />
      {loading ? <Loading /> : error ? <Alert type="error">{error}</Alert> : (
        <>
          <Card style={{ padding: '22px 24px', marginBottom: 24 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 10 }}>HEXACO — my starting point</h3>
            <Alert type="info">A development starting point — never a verdict. Discussed in your orientation debrief.</Alert>
            <div style={{ marginTop: 12, fontSize: '0.92rem' }}>
              Status: <b>{hexacoLabels[data.candidate.hexaco] || data.candidate.hexaco}</b>
            </div>
          </Card>
          <Card style={{ padding: '22px 24px' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 10 }}>Against the Ice Pilot ideal profile</h3>
            <p style={{ fontSize: '0.88rem', color: 'var(--ink-soft)', marginBottom: 12 }}>
              Self-assessments and readiness vs Explorer targets run on compass.hansenbeck.com — the same
              gap engine your future clients will use. Results appear here once your campaigns are set up
              with your class.
            </p>
            <Btn loading={sending} onClick={requestSelfAssessment}>Start self-assessment →</Btn>
            {sent && (
              <div style={{ marginTop: 12 }}>
                <Alert type="success">Your request for a self-assessment has been sent. You’ll be contacted to get started.</Alert>
              </div>
            )}
          </Card>
        </>
      )}
    </BasecampLayout>
  );
}

// ── My Class ───────────────────────────────────────────────────────────────
export function MyClass() {
  const { loading, error, data } = useJourney();
  return (
    <BasecampLayout>
      <PageHeader title="My Class" subtitle={data?.candidate?.className || 'CFA cohort'} />
      {loading ? <Loading /> : error ? <Alert type="error">{error}</Alert> : !data.candidate.className ? (
        <Alert type="warn">You are not assigned to a CFA class yet — your interviewer will assign you at the CFA stage.</Alert>
      ) : (
        <>
          <Alert type="info">Your rope team on the approach march — the cohort forms together, across countries.</Alert>
          <div className="grid-2col" style={{ gap: 24, marginTop: 20 }}>
            <Card style={{ padding: '22px 24px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 12 }}>Classmates</h3>
              <div style={{ fontSize: '0.9rem', lineHeight: 2 }}>
                {data.classmates.map(m => (
                  <div key={m.FullName}>▪ {m.FullName}{m.Country ? ` (${m.Country})` : ''}</div>
                ))}
              </div>
              <div style={{ marginTop: 12, fontSize: '0.82rem', color: 'var(--ink-faint)' }}>
                ▪ Expedition guest day — Aug 2026 (meet the community) — sign up in Community → Events
              </div>
            </Card>
            <Card style={{ padding: '22px 24px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 12 }}>
                Preparation sessions{data.cfaLeader ? ` — led by ${data.cfaLeader}, CFA Leader` : ''}
              </h3>
              <Table
                headers={['#', 'Session (2h · online)', 'Status / date']}
                rows={data.sessions.map(s => [s.SessionNo, s.Title, s.WhenText || s.Status])}
              />
            </Card>
          </div>
          <Card style={{ padding: '18px 24px', marginTop: 24 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 8 }}>Class forum</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--ink-faint)' }}>
              Private to your class — coming with Forums (Phase 4). Until then, your class chats live in the sessions.
            </p>
          </Card>
        </>
      )}
    </BasecampLayout>
  );
}
